import { useState } from "react";
import { Modal } from "./Modal";
import { Alert, Button, Field, Textarea } from "./ui";
import { useUpdateOrg } from "../lib/queries";

/**
 * Suspends an organization: read-only for its members, workloads left
 * running, a reason they will be shown. Shared by the organization page
 * and a case's page, so a suspension that starts from a case is the
 * same suspension -- same reason field, same mail, same audit row -- as one
 * an operator applies on their own judgement. A batch from the queue goes
 * through `SuspendCasesModal`: the same suspension, several at once, behind
 * the authenticator.
 */
export function SuspendOrgModal({
  orgId,
  name,
  open,
  onClose,
  onSuspended,
}: {
  orgId: string;
  name: string;
  open: boolean;
  onClose: () => void;
  /** After the organization is suspended, with the reason -- the case page records its decision here. */
  onSuspended?: (reason: string | null) => void;
}) {
  const update = useUpdateOrg(orgId);
  const [reason, setReason] = useState("");
  const [blockPayment, setBlockPayment] = useState(false);

  function close() {
    update.reset();
    setReason("");
    setBlockPayment(false);
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={`Suspend ${name}`}
      description="Members keep read access, support and top-ups. Deploys and edits are blocked. Workloads are not stopped — they keep running and keep billing."
      dismissable={!update.isPending}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          update.mutate(
            { suspended: true, suspendedReason: reason.trim() || null, blockPayment },
            {
              onSuccess: () => {
                const given = reason.trim() || null;
                close();
                onSuspended?.(given);
              },
            },
          );
        }}
        className="space-y-4"
      >
        {update.error && <Alert>{update.error.message}</Alert>}

        <Field
          label="Reason"
          htmlFor="reason"
          hint="Shown to members when they attempt a blocked action."
        >
          <Textarea
            id="reason"
            rows={3}
            placeholder="Payment overdue since…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>

        {/*
          Off by default, and worth the extra control rather than a rule about
          the kind of suspension. Paying stays open normally because the
          workloads keep running and keep being charged — refusing the money
          while delivering the service is the wrong way round. The cases where
          taking it is itself the problem are ones only the operator can name.
        */}
        <label className="flex items-start gap-2.5 text-sm text-fg">
          <input
            type="checkbox"
            className="mt-0.5 size-3.5 accent-accent"
            checked={blockPayment}
            onChange={(e) => setBlockPayment(e.target.checked)}
          />
          <span>
            Refuse top-ups too
            <span className="mt-0.5 block text-xs text-fg-subtle">
              For suspected fraud, a chargeback or a sanctions concern. Otherwise leave this off:
              their workloads keep running and keep drawing credit, so they need a way to pay for
              what they are still being served.
            </span>
          </span>
        </label>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" disabled={update.isPending} onClick={close}>
            Cancel
          </Button>
          <Button type="submit" variant="danger" loading={update.isPending}>
            Suspend
          </Button>
        </div>
      </form>
    </Modal>
  );
}
